import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Popup } from '@atlaskit/popup';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphDeal_AtlassianSearchPage$key } from './__generated__/searchGraphDeal_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	subheading: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
	lozengeWrapper: {
		paddingInlineStart: token('space.800'),
	},
});

export const SearchGraphDeal = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphDeal_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphDeal_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				isDealOwnersEnabled: {
				type: "Boolean!"
				provider: "is-deal-owners-enabled.relayprovider"
				}
			)
			{
				id
				url
				title
				lastModifiedDate
				allContributors
					@optIn(to: "SearchPerformanceImprovementChanges")
					@skip(if: $isDealOwnersEnabled) {
					id
					name
				}
				entity {
					... on ExternalDeal {
						stage
						dealClosedAt
						lastActivity {
							lastActivityAt
							event
						}
						owners @include(if: $isDealOwnersEnabled){
							thirdPartyUser {
								name
							}
						}
						parent @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalCustomerOrg {
								id
								displayName
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage, formatDate } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const { id, url, entity, title, lastModifiedDate, allContributors } = data;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	const formattedDate = entity?.dealClosedAt
		? formatDate(new Date(entity.dealClosedAt), {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: '';

	const closeOnDateComponent = (
		<Box xcss={styles.content}>
			<FormattedMessage {...messages.closedOn} values={{ date: formattedDate }} />
		</Box>
	);

	const body = <Stack>{entity?.lastActivity?.event}</Stack>;

	const description = (
		<Box>
			<DescriptionBody description={body} />
			<Box xcss={styles.subheading}>
				<LastModified lastModified={entity?.lastActivity?.lastActivityAt} />
			</Box>
		</Box>
	);

	const ownerFromContributors = allContributors?.[0]?.name;

	const ownerFromOwners = () => {
		if (!entity?.owners || !Array.isArray(entity.owners) || entity.owners.length === 0) {
			return undefined;
		}
		return entity.owners[0]?.thirdPartyUser?.name;
	};
	const ownerName = ownerFromOwners() ?? ownerFromContributors;
	const stageText = entity?.stage || '';
	const parent = entity?.parent?.displayName;
	const truncatedStageText = stageText.length > 20 ? `${stageText.slice(0, 20)}...` : stageText;

	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['parent', parent],
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" justifyContent="start" role="listitem">
			<SearchResult
				id={id}
				product={product}
				productSelected={!!selectedProduct}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				onCopyLinkButtonClicked={onCopyLinkButtonClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				icon={icon}
				description={description}
				headerMetadataParts={headerMetadataParts}
				title={enrichExcerpt(title)}
				role="none"
				url={url}
				isThirdParty
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				position={position}
				partial={data}
				appMode={appMode}
				footer={footerComponent}
				hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
				mainContentWidth={mainContentWidth}
			/>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="right-start"
				content={() => closeOnDateComponent}
				trigger={(triggerProps) => (
					<Tooltip content={stageText} position="auto">
						<Button
							{...triggerProps}
							isSelected={isOpen}
							appearance="subtle"
							spacing="compact"
							onClick={() => setIsOpen(!isOpen)}
						>
							<Box xcss={styles.lozengeWrapper}>
								<Lozenge>{truncatedStageText}</Lozenge>
							</Box>
						</Button>
					</Tooltip>
				)}
			/>
		</Flex>
	);
};
